import {
  getTrackingDispatchers,
  TrackingEvent,
} from '@owc/connection-aem-tracking';
import { UserAssociatedVehicles } from '@owc/connection-wko-manager';
import { Step } from '@tradein/api';
import setup from '../composables/setup';

const StepIds = {
  vehicledata: 'vehicledata',
  vinsearch: 'vinsearch',
  modelsearch: 'modelsearch',
  mesearch: 'mesearch',
  emailrequest: 'emailrequest',
  emailconfirmation: 'emailconfirmation',
  dealerselection: 'dealerselection',
  dealercontact: 'dealercontact',
  dealerrequest: 'dealerrequest',
  dealerconfirmation: 'dealerconfirmation',
  applicationerror: 'applicationerror',
  valuationrejection: 'valuationrejection',
  ratelimit: 'ratelimit',
  colorselection: 'colorselection',
  cityselection: 'cityselection',
  emaildealerconfirmation: 'emaildealerconfirmation',
  applicationmaintenance: 'applicationmaintenance',
  licenseplateidentification: 'licenseplateidentification',
  emailrequesterror: 'emailrequesterror',
  dealerrequesterror: 'dealerrequesterror',
  residualvalueerror: 'residualvalueerrror',
};

interface EventDetail {
  category: string;
  action: string;
  label: string | undefined;
  retailerId: string | undefined;
  errorDescription?: string[];
  URL?: string;
  vehicle?: {
    baumusterId?: string | undefined;
    modelCode?: string | undefined;
    priceTotal?: string | undefined;
  };
  form?: {
    name: string | undefined;
    requestType: string;
    id: string | undefined;
    requestId: string | undefined;
  };
}

interface PageDetail {
  name: string;
  type: string;
  message?: string;
}

class TrackingService {
  private userAssociatedVehicles: UserAssociatedVehicles[] = [];
  private vehicleBrand: string | undefined;
  private vehicleModel: string | undefined;
  private vehicleYear: string | undefined;
  private vehicleBaumuster: string | undefined;
  private vehicleCode: string | undefined;
  private vehiclePrice: string | undefined;
  private formName: string | undefined;
  private formRequestType = 'trade_in';
  private formId: string | undefined;
  private tradeInId: string | undefined;
  private retailerId: string | undefined;
  private currentStepId: string | undefined;

  private push(
    name: string,
    clickDetail?: EventDetail,
    pageDetail?: PageDetail,
  ): void {
    const { applicationInfos } = setup();

    const trackingEvent: TrackingEvent = {
      event: name,
      application: {
        id: 'trade_in',
        version: applicationInfos.value
          ? applicationInfos.value.buildVersion
          : '0',
        parentApplicationId: 'aem',
      },
      ...(clickDetail && { eventDetail: clickDetail }),
      ...(pageDetail && { page: pageDetail }),
    };

    if (applicationInfos.value.environment !== 'prod') {
      this.consoleSyntaxHighlight(trackingEvent);
    }

    getTrackingDispatchers().then((dispatchers) => {
      dispatchers.pushTrackingEvent(trackingEvent);
    });
  }

  public publishClick(
    eventCategory: string,
    clickAction: string,
    eventLabel?: string,
    errorDescriptions?: string[],
    url?: string,
  ): void {
    const detail: EventDetail = {
      category: eventCategory,
      action: clickAction.includes('form_')
        ? (this.getLabel(eventLabel) as string)
        : clickAction,
      label: clickAction.includes('form_')
        ? clickAction
        : this.getLabel(eventLabel),
      retailerId: this.retailerId,
      ...(errorDescriptions && { errorDescription: errorDescriptions }),
      ...(url && { URL: url }),
      ...((this.vehicleBaumuster || this.vehicleCode || this.vehiclePrice) && {
        vehicle: {
          ...(this.vehicleBaumuster && { baumusterId: this.vehicleBaumuster }),
          ...(this.vehicleCode && { modelCode: this.vehicleCode }),
          ...(this.vehiclePrice && { priceTotal: this.vehiclePrice }),
        },
      }),
      ...(clickAction.includes('form_') && {
        form: {
          name: this.formName,
          requestType: this.formRequestType,
          id: this.formId,
          requestId: this.tradeInId,
        },
      }),
    };

    this.push('click', detail, undefined);
  }

  public publishError() {
    this.publishClick(`impression`, `error_modal.${this.currentStepId}`);
  }

  public setStepInformation(step: Step): void {
    try {
      if (step?.data?.summary) {
        if (step.data.summary.headline && step.data.summary.copy) {
          this.vehicleBrand = step.data.summary.headline
            .split(' ')[0]
            .toLowerCase();

          if (!this.vehicleModel) {
            this.vehicleModel = step.data.summary.copy
              .split(' ')
              .join('_')
              .toLowerCase();
          }
        } else {
          this.vehicleBrand = undefined;
          this.vehicleModel = undefined;
        }

        if (step.data.summary.sections) {
          for (const section of step.data.summary.sections) {
            if (section.summaryItems) {
              for (const item of section.summaryItems) {
                if (item.key === 'FirstRegistration') {
                  this.vehicleYear = item.value.split('.')[1];
                }

                if (item.key === 'Vin') {
                  const matchingUserAssociatedVehicle =
                    this.userAssociatedVehicles?.filter(
                      (vehicle) => vehicle.finOrVin === item.value,
                    )[0];

                  if (matchingUserAssociatedVehicle) {
                    this.vehicleCode = matchingUserAssociatedVehicle.modelId;
                    this.vehicleBaumuster =
                      matchingUserAssociatedVehicle.bm7 as string;
                  }
                }
              }
            }
          }
        } else {
          this.vehicleYear = undefined;
          this.vehicleModel = undefined;
          this.vehicleCode = undefined;
        }

        if (step.data.residualValueAnalytics) {
          this.vehiclePrice = step.data.residualValueAnalytics.toString();
        } else {
          this.vehiclePrice = undefined;
        }
      }

      if (step?.stepId !== 'ApplicationError') {
        this.currentStepId = step?.stepId;
      }

      if (
        step?.subStepId === StepIds.vinsearch ||
        step?.subStepId === StepIds.modelsearch ||
        step?.subStepId === StepIds.mesearch
      ) {
        this.vehicleBrand = undefined;
        this.vehicleYear = undefined;
        this.vehicleModel = undefined;
        this.vehicleCode = undefined;
        this.vehicleBaumuster = undefined;
      }

      this.formName = step?.form && step?.stepId ? step?.stepId : undefined;
      this.formId = step?.form && step?.stepId ? step?.stepId : undefined;
      this.tradeInId = step?.data?.tradeinId || undefined;
      this.retailerId = step?.data?.dealerInfo?.baseInfo?.outletId || undefined;
    } catch (error) {
      console.warn(error);
    }
  }

  public setUserAssociatedVehicles(vehicles: UserAssociatedVehicles[]): void {
    this.userAssociatedVehicles = vehicles;
  }

  public getFirstInteraction(target: HTMLElement): void {
    let timeout: ReturnType<typeof setTimeout>;

    const intersectionObserverOptions = {
      root: null,
      rootMargin: '0px 0px -50% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          timeout = setTimeout(() => {
            this.publishClick(`impression`, `step.tradein_start`);

            observer.disconnect();
          }, 2000);
        } else {
          clearTimeout(timeout);
        }
      });
    }, intersectionObserverOptions);

    if (target) {
      observer.observe(target);
    }
  }

  private getLabel(eventLabel: string | undefined) {
    if (eventLabel) {
      return eventLabel;
    } else if (this.vehicleBrand && this.vehicleModel && this.vehicleYear) {
      return `${this.vehicleBrand}|${this.vehicleModel}|${this.vehicleYear}`;
    } else {
      return undefined;
    }
  }

  private consoleSyntaxHighlight(json: any) {
    if (typeof json != 'string') {
      json = JSON.stringify(json, undefined, '\t');
    }

    const arr = [],
      _string = 'color:#00adef',
      _number = 'color:#00adef',
      _boolean = 'color:#00adef',
      _null = 'color:#00adef',
      _key = 'color:white';

    json = json.replace(
      // eslint-disable-next-line no-useless-escape
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      function (match: any) {
        let style = _number;
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            style = _key;
          } else {
            style = _string;
          }
        } else if (/true|false/.test(match)) {
          style = _boolean;
        } else if (/null/.test(match)) {
          style = _null;
        }
        arr.push(style);
        arr.push('');
        return '%c' + match + '%c';
      },
    );

    arr.unshift(json);

    // eslint-disable-next-line prefer-spread
  }
}

export const trackingService = new TrackingService();
